import type { ApolloError } from "@apollo/client";
import { useMutation } from "@apollo/client";
import _ from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { REMOVE_VULN_MUTATION } from "./queries";
import type {
  IRemoveVulnerabilities,
  TRemoveVulnerabilityResult,
} from "./types";
import { removeVulnerabilitiesChunked } from "./utils";

import type { IVulnRowAttr } from "features/vulnerabilities/types";
import type {
  RemoveVulnerabilityJustification,
  RemoveVulnerabilityMutationMutation,
} from "gql/graphql";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

const useRemoveVulnerabilities = (
  vulnerabilities: IVulnRowAttr[],
  onRemoveVulnerabilities: (data: RemoveVulnerabilityMutationMutation) => void,
): IRemoveVulnerabilities => {
  const { t } = useTranslation();

  const [removeVulnerability] = useMutation(REMOVE_VULN_MUTATION);

  const removeVulnerabilitiesMutation = useCallback(
    async (justification: string): Promise<TRemoveVulnerabilityResult[][]> => {
      const vulnerabilitiesByFinding = _.groupBy(
        vulnerabilities,
        (vulnerability): string => vulnerability.findingId,
      );
      const requestedChunks = Object.entries(vulnerabilitiesByFinding).map(
        ([findingId, chunkedVulnerabilities]: [
          string,
          IVulnRowAttr[],
        ]): (() => Promise<TRemoveVulnerabilityResult[][]>) =>
          async (): Promise<TRemoveVulnerabilityResult[][]> => {
            return Promise.all([
              removeVulnerabilitiesChunked(
                removeVulnerability,
                findingId,
                justification as RemoveVulnerabilityJustification,
                chunkedVulnerabilities.map(
                  (vulnerability): string => vulnerability.id,
                ),
              ),
            ]);
          },
      );

      return requestedChunks.reduce(
        async (
          previousValue,
          currentValue,
        ): Promise<TRemoveVulnerabilityResult[][]> => [
          ...(await previousValue),
          ...(await currentValue()),
        ],
        Promise.resolve<TRemoveVulnerabilityResult[][]>([]),
      );
    },
    [removeVulnerability, vulnerabilities],
  );

  const removeVulnerabilities = useCallback(
    async (justification: string): Promise<void> => {
      try {
        const allResults = await removeVulnerabilitiesMutation(justification);
        const areAllMutationValid = allResults
          .map((results): boolean[] =>
            results.map((result): boolean =>
              Boolean(result.data?.removeVulnerability.success),
            ),
          )
          .reduce(
            (previous: boolean[], current: boolean[]): boolean[] => [
              ...previous,
              ...current,
            ],
            [],
          );
        if (areAllMutationValid.every(Boolean)) {
          onRemoveVulnerabilities({
            removeVulnerability: {
              success: true,
            },
          });
        }
      } catch (requestError: unknown) {
        (requestError as ApolloError).graphQLErrors.forEach((error): void => {
          switch (error.message) {
            case "Invalid, you cannot remove a closed vulnerability": {
              msgError(t("deleteVulns.closedVuln"));
              break;
            }
            case "Invalid, you cannot remove a vulnerability that has already been released": {
              msgError(t("deleteVulns.releasedVuln"));
              break;
            }
            case "Exception - Vulnerability not found": {
              onRemoveVulnerabilities({
                removeVulnerability: {
                  success: false,
                },
              });
              break;
            }
            default: {
              msgError(t("groupAlerts.errorTextsad"));
              Logger.warning(
                "An error occurred deleting vulnerabilities",
                error,
              );
            }
          }
        });
      }
    },
    [removeVulnerabilitiesMutation, onRemoveVulnerabilities, t],
  );

  return { removeVulnerabilities };
};

export { useRemoveVulnerabilities };
